// SKADE-native chat (TOC-kontakt) — GNÅ-paritet: svar, rediger, slett, reaksjoner
// og skrive-indikator. Hendelsesdrevet (ChatEvent-strøm reduseres via reduceChat).
// Gjenbrukes docked + standalone. All tekst via textContent (ingen XSS).
import { reduceChat, type ChatEvent, type ReducedMessage } from '@skade/contracts';
import { bekreft, sporTekst } from './shell.js';

/** Hendelse panelet vil sende (uten actor/tid — fylles her, hub re-stempler actor). */
export type ChatSend = (e: ChatEvent) => void;

export interface ChatOpts {
  onEvent: ChatSend;
  selfId: string;
  embedded?: boolean;
}

const REACTIONS = ['👍', '❤️', '✅', '⚠️'];

export class ChatPanel {
  readonly el: HTMLElement;
  private open = false;
  private listEl!: HTMLElement;
  private typingEl!: HTMLElement;
  private input!: HTMLInputElement;
  private fileInput!: HTMLInputElement;
  private micBtn!: HTMLButtonElement;
  private badge!: HTMLElement;
  private replyBar!: HTMLElement;
  private recorder: MediaRecorder | null = null;
  private recStartMs = 0;
  private log: ChatEvent[] = [];
  private unread = 0;
  private replyTo: string | null = null;
  private editing: string | null = null;
  private lastTypingSent = 0;

  constructor(private opts: ChatOpts) {
    this.el = this.build();
    if (opts.embedded) { this.open = true; this.el.classList.add('open', 'embedded'); }
    setInterval(() => this.renderTyping(), 2000); // utløp av skrive-indikator
  }

  toggle() { this.open = !this.open; this.el.classList.toggle('open', this.open); if (this.open) { this.unread = 0; this.renderBadge(); this.input.focus(); this.scrollEnd(); } }

  /** Ta imot en (fjern eller egen) chat-hendelse og re-render. */
  ingestEvent(e: ChatEvent) {
    this.log.push(e);
    if (this.log.length > 4000) this.log.splice(0, this.log.length - 4000);
    this.render();
    if (!this.open && e.op === 'post' && e.author !== this.opts.selfId) { this.unread++; this.renderBadge(); }
  }

  private emit(e: ChatEvent) { this.log.push(e); this.opts.onEvent(e); this.render(); } // optimistisk lokalt (hub ekko ikke til avsender)

  private build(): HTMLElement {
    const p = document.createElement('div');
    p.className = 'chat-panel';
    const head = document.createElement('div');
    head.className = 'chat-head';
    const title = document.createElement('span'); title.className = 'chat-title'; title.textContent = 'Chat / TOC';
    this.badge = document.createElement('span'); this.badge.className = 'chat-badge';
    head.append(title, this.badge);
    if (!this.opts.embedded) {
      const x = document.createElement('button'); x.className = 'chat-x'; x.textContent = '×'; x.onclick = () => this.toggle();
      head.append(x);
    }
    this.listEl = document.createElement('div'); this.listEl.className = 'chat-list';
    this.typingEl = document.createElement('div'); this.typingEl.className = 'chat-typing';
    this.replyBar = document.createElement('div'); this.replyBar.className = 'chat-replybar';
    const bar = document.createElement('div'); bar.className = 'chat-inputbar';
    this.input = document.createElement('input'); this.input.className = 'chat-input'; this.input.placeholder = 'Melding til nettet …';
    this.input.onkeydown = (e) => { if (e.key === 'Enter') this.send(); else if (e.key === 'Escape') this.cancelCompose(); };
    this.input.oninput = () => this.sendTyping();
    // Vedlegg (bilde/fil) + tale (opptak).
    const attach = document.createElement('button'); attach.className = 'chat-icon'; attach.textContent = '📎'; attach.title = 'Vedlegg (bilde/fil)';
    this.fileInput = document.createElement('input'); this.fileInput.type = 'file'; this.fileInput.accept = 'image/*,application/pdf'; this.fileInput.style.display = 'none';
    this.fileInput.onchange = () => this.attachFile();
    attach.onclick = () => this.fileInput.click();
    this.micBtn = document.createElement('button'); this.micBtn.className = 'chat-icon'; this.micBtn.textContent = '🎤'; this.micBtn.title = 'Talemelding (klikk for å ta opp / stopp)';
    this.micBtn.onclick = () => this.toggleRecord();
    const send = document.createElement('button'); send.className = 'chat-send'; send.textContent = 'Send'; send.onclick = () => this.send();
    bar.append(attach, this.micBtn, this.fileInput, this.input, send);
    p.append(head, this.listEl, this.typingEl, this.replyBar, bar);
    return p;
  }

  private now() { return Date.now(); }

  private send() {
    const text = this.input.value.trim();
    if (!text) return;
    if (this.editing) {
      this.emit({ op: 'edit', id: this.editing, channel: 'net', author: this.opts.selfId, tsMs: this.now(), text });
    } else {
      const id = `c-${this.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
      const e: ChatEvent = { op: 'post', id, channel: 'net', author: this.opts.selfId, tsMs: this.now(), text, kind: 'text' };
      if (this.replyTo) e.replyTo = this.replyTo;
      this.emit(e);
    }
    this.cancelCompose();
  }

  private sendTyping() {
    const t = this.now();
    if (t - this.lastTypingSent < 2500) return; // strup
    this.lastTypingSent = t;
    this.opts.onEvent({ op: 'typing', channel: 'net', author: this.opts.selfId, tsMs: t });
  }

  private cancelCompose() { this.input.value = ''; this.replyTo = null; this.editing = null; this.renderReplyBar(); }

  /** Last opp media til hubben (/media, sesjons-cookie). Returnerer URL eller null. */
  private async uploadMedia(blob: Blob): Promise<string | null> {
    try {
      const r = await fetch('/media', { method: 'POST', headers: { 'content-type': blob.type }, body: blob, credentials: 'include' });
      if (!r.ok) return null;
      const j = await r.json() as { url?: string };
      return j.url ?? null;
    } catch { return null; }
  }

  private postAttachment(kind: 'voice' | 'image' | 'file', url: string, durationS?: number) {
    const id = `c-${this.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    const e: ChatEvent = { op: 'post', id, channel: 'net', author: this.opts.selfId, tsMs: this.now(), text: '', kind, attachmentUrl: url, durationS };
    if (this.replyTo) e.replyTo = this.replyTo;
    this.emit(e);
    this.cancelCompose();
  }

  private async attachFile() {
    const f = this.fileInput.files?.[0];
    this.fileInput.value = '';
    if (!f) return;
    const url = await this.uploadMedia(f);
    if (!url) { window.alert('Opplasting feilet (krever innlogget backend).'); return; }
    this.postAttachment(f.type.startsWith('image/') ? 'image' : 'file', url);
  }

  private async toggleRecord() {
    if (this.recorder) { this.recorder.stop(); return; } // stopper pågående opptak
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      this.recStartMs = this.now();
      rec.ondataavailable = (ev) => { if (ev.data.size) chunks.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        this.recorder = null; this.micBtn.classList.remove('recording'); this.micBtn.textContent = '🎤';
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const durationS = Math.round((this.now() - this.recStartMs) / 1000);
        const url = await this.uploadMedia(blob);
        if (url) this.postAttachment('voice', url, durationS);
        else window.alert('Taleopplasting feilet (krever innlogget backend).');
      };
      this.recorder = rec; this.micBtn.classList.add('recording'); this.micBtn.textContent = '■';
      rec.start();
    } catch { window.alert('Ingen mikrofon-tilgang.'); }
  }

  private react(id: string, emoji: string, has: boolean) {
    this.emit({ op: 'react', id, channel: 'net', author: this.opts.selfId, tsMs: this.now(), emoji, add: !has });
  }
  private del(id: string) {
    bekreft({
      tittel: 'Slette meldingen?',
      tekst: 'Meldingen fjernes for alle på nettet.',
      bekreftTekst: 'Slett',
      onBekreft: () => this.emit({ op: 'delete', id, channel: 'net', author: this.opts.selfId, tsMs: this.now() }),
    });
  }
  private startEdit(m: ReducedMessage) { this.editing = m.id; this.replyTo = null; this.input.value = m.text; this.input.focus(); this.renderReplyBar(); }
  private startReply(m: ReducedMessage) { this.replyTo = m.id; this.editing = null; this.input.focus(); this.renderReplyBar(); }

  private renderBadge() { this.badge.textContent = this.unread ? String(this.unread) : ''; this.badge.classList.toggle('show', this.unread > 0); }

  private render() {
    const view = reduceChat(this.log);
    this.byId = new Map(view.messages.map((m) => [m.id, m]));
    this.listEl.innerHTML = '';
    for (const m of view.messages) this.listEl.append(this.row(m));
    this.renderTyping();
    this.scrollEnd();
  }
  private byId = new Map<string, ReducedMessage>();

  private row(m: ReducedMessage): HTMLElement {
    const row = document.createElement('div');
    row.className = 'chat-msg' + (m.author === this.opts.selfId ? ' mine' : '');
    const meta = document.createElement('div'); meta.className = 'chat-meta';
    meta.textContent = `${short(m.author)} · ${new Date(m.tsMs).toISOString().slice(11, 16)}Z${m.editedMs ? ' · redigert' : ''}`;
    row.append(meta);
    if (m.replyTo) {
      const q = document.createElement('div'); q.className = 'chat-quote';
      const src = this.byId.get(m.replyTo);
      q.textContent = src ? `↩ ${short(src.author)}: ${src.deleted ? '(slettet)' : src.text.slice(0, 80)}` : '↩ svar';
      row.append(q);
    }
    if (m.deleted) {
      const body = document.createElement('div'); body.className = 'chat-text deleted'; body.textContent = '(melding slettet)';
      row.append(body);
    } else if (m.kind === 'voice' && m.attachmentUrl) {
      const wrap = document.createElement('div'); wrap.className = 'chat-media';
      const au = document.createElement('audio'); au.controls = true; au.src = m.attachmentUrl; au.preload = 'none';
      wrap.append(au);
      if (m.durationS) wrap.append(span('chat-dur', `🎤 ${m.durationS}s`));
      row.append(wrap);
    } else if (m.kind === 'image' && m.attachmentUrl) {
      const img = document.createElement('img'); img.className = 'chat-image'; img.src = m.attachmentUrl; img.loading = 'lazy';
      img.onclick = () => window.open(m.attachmentUrl, '_blank', 'noopener');
      row.append(img);
    } else if (m.kind === 'file' && m.attachmentUrl) {
      const a = document.createElement('a'); a.className = 'chat-file'; a.href = m.attachmentUrl; a.target = '_blank'; a.rel = 'noopener';
      a.textContent = '📄 Åpne vedlegg';
      row.append(a);
    } else {
      const body = document.createElement('div'); body.className = 'chat-text'; body.textContent = m.text;
      row.append(body);
    }
    // Reaksjons-chips
    const reacts = Object.entries(m.reactions);
    if (reacts.length) {
      const rc = document.createElement('div'); rc.className = 'chat-reacts';
      for (const [emoji, authors] of reacts) {
        const chip = document.createElement('button'); chip.className = 'chat-react-chip' + (authors.includes(this.opts.selfId) ? ' on' : '');
        chip.textContent = `${emoji} ${authors.length}`;
        chip.onclick = () => this.react(m.id, emoji, authors.includes(this.opts.selfId));
        rc.append(chip);
      }
      row.append(rc);
    }
    if (!m.deleted) row.append(this.actions(m));
    return row;
  }

  private actions(m: ReducedMessage): HTMLElement {
    const a = document.createElement('div'); a.className = 'chat-actions';
    const btn = (label: string, title: string, run: () => void) => {
      const b = document.createElement('button'); b.className = 'chat-act'; b.textContent = label; b.title = title; b.onclick = run; return b;
    };
    a.append(btn('↩', 'Svar', () => this.startReply(m)));
    const rBtn = btn('☺', 'Reager', () => {
      const cur = REACTIONS.map((e) => `${e}`).join(' ');
      sporTekst({
        tittel: 'Reager',
        tekst: `Reager med: ${cur}`,
        verdi: '👍',
        onSvar: (pick) => {
          if (pick && pick.trim()) { const emoji = pick.trim(); this.react(m.id, emoji, (m.reactions[emoji] ?? []).includes(this.opts.selfId)); }
        },
      });
    });
    a.append(rBtn);
    if (m.author === this.opts.selfId) {
      a.append(btn('✎', 'Rediger', () => this.startEdit(m)), btn('🗑', 'Slett', () => this.del(m.id)));
    }
    return a;
  }

  private renderReplyBar() {
    this.replyBar.innerHTML = '';
    const label = this.editing ? 'Redigerer melding' : this.replyTo ? `Svarer på ${short(this.byId.get(this.replyTo)?.author ?? '')}` : '';
    this.replyBar.classList.toggle('show', !!label);
    if (!label) return;
    const s = document.createElement('span'); s.textContent = label;
    const x = document.createElement('button'); x.className = 'chat-act'; x.textContent = '×'; x.onclick = () => this.cancelCompose();
    this.replyBar.append(s, x);
  }

  private renderTyping() {
    const view = reduceChat(this.log);
    const now = Date.now();
    const who = view.typing.filter((t) => t.author !== this.opts.selfId && now - t.tsMs < 5000).map((t) => short(t.author));
    this.typingEl.textContent = who.length ? `${who.join(', ')} skriver …` : '';
  }

  private scrollEnd() { this.listEl.scrollTop = this.listEl.scrollHeight; }
}

function short(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
